import { AnchorTitle } from "#components/ui/anchor-title"
import { Callout } from "#components/ui/callout"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "#components/ui/card"
import { Separator } from "#components/ui/separator"
import { Body, H3 } from "#components/ui/typography"
import { useI18n } from "#hooks/use-i18n"
import { Palette, Pencil, Plus, Trash2 } from "lucide-react"

export function CategoriesHelp() {
  const { t } = useI18n()

  return (
    <div className="space-y-6">
      <div>
        <H3 className="mb-2">{t("pages.help.categories.overview.title")}</H3>
        <Body className="text-muted-foreground">
          {t("pages.help.categories.overview.description")}
        </Body>
      </div>

      <Card id="categories-table">
        <CardHeader>
          <CardTitle>
            <AnchorTitle id="categories-table">
              {t("pages.help.categories.table.title")}
            </AnchorTitle>
          </CardTitle>
          <CardDescription>{t("pages.help.categories.table.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.categories.table.columns.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.categories.table.columns.description")}
            </Body>
          </div>

          <Separator />

          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.categories.table.sorting.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.categories.table.sorting.description")}
            </Body>
          </div>
        </CardContent>
      </Card>

      <Card id="categories-actions">
        <CardHeader>
          <CardTitle>
            <AnchorTitle id="categories-actions">
              {t("pages.help.categories.actions.title")}
            </AnchorTitle>
          </CardTitle>
          <CardDescription>{t("pages.help.categories.actions.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-start gap-3">
            <Plus className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
            <div className="flex-1">
              <Body className="font-semibold mb-1">
                {t("pages.help.categories.actions.create.title")}
              </Body>
              <Body className="text-muted-foreground text-sm">
                {t("pages.help.categories.actions.create.description")}
              </Body>
            </div>
          </div>

          <Separator />

          <div className="flex items-start gap-3">
            <Pencil className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
            <div className="flex-1">
              <Body className="font-semibold mb-1">
                {t("pages.help.categories.actions.edit.title")}
              </Body>
              <Body className="text-muted-foreground text-sm">
                {t("pages.help.categories.actions.edit.description")}
              </Body>
            </div>
          </div>

          <Separator />

          <div className="flex items-start gap-3">
            <Trash2 className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
            <div className="flex-1">
              <Body className="font-semibold mb-1">
                {t("pages.help.categories.actions.delete.title")}
              </Body>
              <Body className="text-muted-foreground text-sm">
                {t("pages.help.categories.actions.delete.description")}
              </Body>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card id="categories-form">
        <CardHeader>
          <CardTitle>
            <AnchorTitle id="categories-form">{t("pages.help.categories.form.title")}</AnchorTitle>
          </CardTitle>
          <CardDescription>{t("pages.help.categories.form.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Callout variant="tip">
            <p>
              <strong>Tip:</strong> Choose a distinct color for each category to make them easily
              recognizable in charts and visualizations.
            </p>
          </Callout>

          <div>
            <Body className="font-semibold mb-2">{t("pages.help.categories.form.name.title")}</Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.categories.form.name.description")}
            </Body>
          </div>

          <Separator />

          <div>
            <Body className="font-semibold mb-2">
              {t("pages.help.categories.form.description.title")}
            </Body>
            <Body className="text-muted-foreground text-sm">
              {t("pages.help.categories.form.description.description")}
            </Body>
          </div>

          <Separator />

          <div className="flex items-start gap-3">
            <Palette className="w-5 h-5 mt-0.5 text-primary flex-shrink-0" />
            <div className="flex-1">
              <Body className="font-semibold mb-1">
                {t("pages.help.categories.form.color.title")}
              </Body>
              <Body className="text-muted-foreground text-sm">
                {t("pages.help.categories.form.color.description")}
              </Body>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card id="categories-permissions">
        <CardHeader>
          <CardTitle>
            <AnchorTitle id="categories-permissions">
              {t("pages.help.categories.permissions.title")}
            </AnchorTitle>
          </CardTitle>
          <CardDescription>{t("pages.help.categories.permissions.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Body className="text-muted-foreground text-sm">
            {t("pages.help.categories.permissions.content")}
          </Body>
        </CardContent>
      </Card>
    </div>
  )
}
